import dayjs from 'dayjs';
import getFileExtension from 'utils/getFileExtension';
import isImageFormat from 'utils/isImageFormat';
import { normalizeRotation } from 'utils/thumbnailRotation';
import { getFolderDisplayName } from 'scenes/ProjectBank/utils/getFolderDisplayName';

/** Format keys in the display / type-sort rank order. */
export const BANK_FORMAT_KEYS = [
  'folder',
  'image',
  'video',
  'pdf',
  'document',
  'spreadsheet',
  'other',
];

/** Item kinds produced by toBankItem (tilaajavastuu is built by its own tile). */
export const BANK_ITEM_KINDS = {
  PROJECT: 'project',
  FOLDER: 'folder',
  VIRTUAL_FOLDER: 'virtualFolder',
  FILE: 'file',
  VIRTUAL_FILE: 'virtualFile',
  TILAAJAVASTUU: 'tilaajavastuu',
};

const VIDEO_EXTENSIONS = ['mp4', 'mov', 'avi', 'webm', 'mkv'];
const SPREADSHEET_EXTENSIONS = ['xls', 'xlsx', 'csv', 'ods'];
const DOCUMENT_EXTENSIONS = ['doc', 'docx', 'txt', 'rtf', 'odt'];

const identity = (key) => key;

/** The reactions of an item without any (one shared array, never mutated). */
const EMPTY_REACTIONS = [];

const toLowerString = (value) =>
  typeof value === 'string' ? value.toLowerCase() : '';

/**
 * Resolves the coarse format bucket of an item from its mime type first and
 * its extension second. Folders (including sub-projects) are always 'folder'.
 * @param {{type?: string, mimeType?: string, extension?: string}} params
 * @returns {string} one of BANK_FORMAT_KEYS
 */
export function getBankFormatKey({ type, mimeType, extension } = {}) {
  if (type === 'FOLDER') return 'folder';
  const mime = toLowerString(mimeType);
  const ext = toLowerString(extension);

  if (mime.startsWith('video/') || VIDEO_EXTENSIONS.includes(ext)) {
    return 'video';
  }
  if (mime.includes('pdf') || ext === 'pdf') {
    return 'pdf';
  }
  if (mime.includes('spreadsheet') || SPREADSHEET_EXTENSIONS.includes(ext)) {
    return 'spreadsheet';
  }
  if (
    mime.includes('wordprocessingml') ||
    mime.includes('msword') ||
    DOCUMENT_EXTENSIONS.includes(ext)
  ) {
    return 'document';
  }
  if (isImageFormat(ext) || mime.startsWith('image/')) {
    return 'image';
  }
  return 'other';
}

/**
 * Builds avatar initials from a first and last name ('JS'); '' when both empty.
 * @param {string} [firstName]
 * @param {string} [lastName]
 * @returns {string}
 */
export function getAuthorInitials(firstName, lastName) {
  const first = typeof firstName === 'string' ? firstName.trim() : '';
  const last = typeof lastName === 'string' ? lastName.trim() : '';
  return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
}

const toAuthor = (user, pictureKeyByUserId = {}) => {
  if (!user || typeof user !== 'object') return null;
  const { firstName, lastName } = user;
  const name = [firstName, lastName]
    .filter((part) => typeof part === 'string' && part.trim() !== '')
    .map((part) => part.trim())
    .join(' ')
    .trim();
  if (!name) return null;
  const id = user.id ?? null;
  // The profile picture: resolved per user id by the selectors (own picture
  // or the personal resource's), else whatever the joined user carries.
  const pictureKey =
    (id != null && pictureKeyByUserId && pictureKeyByUserId[id]) ||
    (user.picture && user.picture.key) ||
    null;
  return {
    id,
    name,
    initials: getAuthorInitials(firstName, lastName),
    pictureKey,
  };
};

const resolveKind = (raw, type, kindHint) => {
  if (kindHint === BANK_ITEM_KINDS.PROJECT) return BANK_ITEM_KINDS.PROJECT;
  if (kindHint === BANK_ITEM_KINDS.TILAAJAVASTUU) {
    return BANK_ITEM_KINDS.TILAAJAVASTUU;
  }
  const isVirtual = raw.isVirtualProjectBankForm === true;
  if (type === 'FOLDER') {
    return isVirtual ? BANK_ITEM_KINDS.VIRTUAL_FOLDER : BANK_ITEM_KINDS.FOLDER;
  }
  return isVirtual ? BANK_ITEM_KINDS.VIRTUAL_FILE : BANK_ITEM_KINDS.FILE;
};

// Verbatim from PrivateFolderItem.jsx: translationKey → bank.translated_items.*,
// falling back to the raw name when the key has no translation. Private items
// must never go through getFolderDisplayName (its system-folder map would
// translate user-named private folders such as "images").
const getPrivateDisplayName = (raw, t) => {
  if (!raw.translationKey) return raw.name;
  const translationKey = `bank.translated_items.${raw.translationKey}`;
  const translated = t(translationKey);
  return translated !== translationKey ? translated : raw.name;
};

const resolveDisplayName = (raw, mode, t) => {
  const displayName =
    mode === 'PRIVATE'
      ? getPrivateDisplayName(raw, t)
      : getFolderDisplayName(raw, t);
  return typeof displayName === 'string' ? displayName : '';
};

const resolveExtension = (raw, type) => {
  if (type !== 'FILE' || typeof raw.name !== 'string') return '';
  if (!raw.name.includes('.')) return '';
  const extension = getFileExtension(raw.name);
  return typeof extension === 'string' ? extension.toLowerCase() : '';
};

const resolveChipLabel = (kind, extension, t) => {
  switch (kind) {
    case BANK_ITEM_KINDS.PROJECT:
      return t('bank.type_project');
    case BANK_ITEM_KINDS.FOLDER:
    case BANK_ITEM_KINDS.VIRTUAL_FOLDER:
    case BANK_ITEM_KINDS.TILAAJAVASTUU:
      return t('bank.type_folder');
    default:
      return extension.toUpperCase() || 'FILE';
  }
};

const isFiniteNumber = (value) =>
  typeof value === 'number' && Number.isFinite(value);

const resolveSize = ({ raw, id, type, kind, mode, fileSizesById }) => {
  const isVirtual = raw.isVirtualProjectBankForm === true;
  if (type !== 'FILE' || isVirtual || kind === BANK_ITEM_KINDS.PROJECT) {
    return { size: null, sizeStatus: 'na' };
  }
  const rawSize = raw.file?.size ?? raw.size;
  if (isFiniteNumber(rawSize)) {
    return { size: rawSize, sizeStatus: 'known' };
  }
  if (mode !== 'PUBLIC') {
    return { size: null, sizeStatus: 'na' };
  }
  const cache = fileSizesById || {};
  if (Object.prototype.hasOwnProperty.call(cache, id)) {
    const cached = cache[id];
    if (isFiniteNumber(cached)) {
      return { size: cached, sizeStatus: 'known' };
    }
    return { size: null, sizeStatus: 'unresolved' };
  }
  return { size: null, sizeStatus: 'unknown' };
};

const resolveCreatedAt = (raw) => {
  const createdAt = raw.createdAt || null;
  if (!createdAt) return { createdAt: null, createdAtMs: 0 };
  const parsed = dayjs(createdAt);
  const createdAtMs = parsed.isValid() ? parsed.valueOf() : 0;
  return {
    createdAt,
    createdAtMs: Number.isFinite(createdAtMs) ? createdAtMs : 0,
  };
};

// The stored total of the gallery's image-view log on a public
// FilePermission (bumped by the image-view endpoint); 0 when absent or not a
// positive number.
const resolveViewCount = (raw) => {
  const viewCount = Number(raw.viewCount);
  return Number.isFinite(viewCount) && viewCount > 0 ? viewCount : 0;
};

const resolveAuthor = (raw, mode, users, pictureKeyByUserId) => {
  if (mode === 'PRIVATE') {
    const user = raw.createdBy && users ? users[raw.createdBy] : null;
    return toAuthor(user, pictureKeyByUserId);
  }
  return toAuthor(raw.user, pictureKeyByUserId);
};

// The thumbnail descriptor useBankThumbnail resolves. Images have one in
// either bank. A public video has one from the same endpoint as a public
// image: the short looping .webm the backend renders of every uploaded
// video, which the gallery plays; `media: 'video'` says it is drawn in a
// <video>, never in an <img>. Only a video/* mime type gets one, the
// endpoint's own rule: it signs the .webm path only for those, and fails
// for a file without a mime type. Private videos have no rendered
// thumbnail, and a virtual form mirror is no file of its own.
const resolveThumbnail = ({
  source,
  id,
  mode,
  formatKey,
  mimeType,
  isPublicFile,
}) => {
  if (formatKey === 'image') {
    return mode === 'PRIVATE'
      ? { source: 'private', projectId: source.projectId, fileId: id }
      : { source: 'public', filePermissionId: id };
  }
  if (
    formatKey === 'video' &&
    isPublicFile &&
    toLowerString(mimeType).startsWith('video/')
  ) {
    return { source: 'public', filePermissionId: id, media: 'video' };
  }
  return null;
};

/**
 * Whether a BankItem thumbnail descriptor is a video's (see toBankItem): its
 * URL is a looping .webm, to be drawn in a <video>, not in an <img>.
 * @param {?Object} thumbnail BankItem `thumbnail`
 * @returns {boolean}
 */
export function isVideoThumbnail(thumbnail) {
  return !!thumbnail && thumbnail.media === 'video';
}

/**
 * Normalizes a public FilePermission, a virtual form item or a private bank
 * item into the BankItem projection consumed by the cards, rows, sorts and
 * filters. Pure; `raw` is kept untouched on the result for every handler.
 * @param {Object} raw the source item
 * @param {Object} ctx
 * @param {'PUBLIC'|'PRIVATE'} [ctx.mode='PUBLIC']
 * @param {Function} [ctx.t] i18next translation function (namespace 'common')
 * @param {Object} [ctx.users] projectUsers entities keyed by id (PRIVATE)
 * @param {Object} [ctx.pictureKeyByUserId] profile picture file key per user id
 * @param {Object} [ctx.fileSizesById] public size cache: id → bytes | null
 * @param {Object} [ctx.mediaTagLinksByFileId] media tag links grouped by file id
 * @param {Object} [ctx.insufficientDocsByProjectId] project id → count
 * @param {Object} [ctx.reactionsByFileId] emoji reactions per public file id
 *   (see selectBankReactionsByFileId): `[{emoji, count, own, latestAt, userIds, names}]`
 * @param {boolean} [ctx.hideAuthors=false] no uploader is shown (a
 *   gallery-only user without "view media adder info"): `author` is null and
 *   `authorHidden` true, which the cards and rows tell apart from an unknown
 *   author
 * @param {boolean} [ctx.galleryOnly=false] the user may use only the images
 *   folder (a gallery-only user): folders are not selectable
 * @param {Object} [ctx.floorplanPinsByFileId] the gallery view's pins per
 *   public file id (selectBankFloorplanPinsByFileId): `{count, names}`
 * @param {'project'|'folder'|'file'|'tilaajavastuu'} [ctx.kindHint]
 * @returns {Object} BankItem
 */
export function toBankItem(raw, ctx = {}) {
  const {
    mode = 'PUBLIC',
    t = identity,
    users = {},
    pictureKeyByUserId = {},
    fileSizesById = {},
    mediaTagLinksByFileId = {},
    insufficientDocsByProjectId = {},
    reactionsByFileId = {},
    hideAuthors = false,
    galleryOnly = false,
    floorplanPinsByFileId = {},
    kindHint,
  } = ctx;
  const source = raw || {};
  const id = source.id;
  const type = source.type;
  const kind = resolveKind(source, type, kindHint);
  const displayName = resolveDisplayName(source, mode, t);
  const rawName = typeof source.name === 'string' ? source.name : '';
  const extension = resolveExtension(source, type);
  const rawMimeType = source.file?.mimeType ?? source.mimeType;
  const mimeType = typeof rawMimeType === 'string' ? rawMimeType : '';
  const formatKey = getBankFormatKey({ type, mimeType, extension });
  const projectId =
    source.project?.id ?? source.project ?? source.projectId ?? null;

  const isReadOnly = source.isReadOnly === true;
  const isVirtual = source.isVirtualProjectBankForm === true;
  const disabled = !!(source.isDeleting || source.isBeingTransferred);
  const isTilaajavastuu = kind === BANK_ITEM_KINDS.TILAAJAVASTUU;
  // A gallery-only user selects files only: nothing a selection is for takes
  // their folders (the API zips no folders for them, and folders are not
  // theirs to change).
  const isSelectable =
    !isTilaajavastuu &&
    !disabled &&
    (mode === 'PUBLIC'
      ? type === 'FILE'
        ? !isReadOnly
        : kind !== BANK_ITEM_KINDS.PROJECT && !galleryOnly
      : type === 'FILE');
  const hasMenu =
    !isTilaajavastuu &&
    (mode === 'PUBLIC' ? !disabled && !isReadOnly && !isVirtual : !disabled);

  const { createdAt, createdAtMs } = resolveCreatedAt(source);

  // Reactions and view counts exist on public FilePermissions only (the
  // gallery's interactions and image-view log); folders, private items and
  // virtual form mirrors have none.
  const isPublicFile = mode === 'PUBLIC' && type === 'FILE' && !isVirtual;
  const reactions = isPublicFile
    ? (reactionsByFileId || {})[id] || EMPTY_REACTIONS
    : EMPTY_REACTIONS;

  return {
    id,
    raw,
    mode,
    type,
    kind,
    displayName,
    searchText: `${displayName} ${rawName}`.toLowerCase(),
    extension,
    mimeType,
    formatKey,
    chipLabel: resolveChipLabel(kind, extension, t),
    ...resolveSize({ raw: source, id, type, kind, mode, fileSizesById }),
    createdAt,
    createdAtMs,
    author: hideAuthors
      ? null
      : resolveAuthor(source, mode, users, pictureKeyByUserId),
    authorHidden: Boolean(hideAuthors),
    thumbnail: resolveThumbnail({
      source,
      id,
      mode,
      formatKey,
      mimeType,
      isPublicFile,
    }),
    // The quarter turns the viewer saved on a photo (0, 90, 180 or 270),
    // which its thumbnails turn their picture by. A video's thumbnail is its
    // own clip and never turns.
    rotation:
      isPublicFile && formatKey === 'image'
        ? normalizeRotation(source.rotation)
        : 0,
    mediaTagIds:
      mode === 'PUBLIC' && type === 'FILE'
        ? ((mediaTagLinksByFileId || {})[id] || [])
            .filter((link) => link && !link.deletedAt)
            .map((link) => link.mediaTagId)
        : [],
    flags: { isReadOnly, isVirtual, disabled, isSelectable, hasMenu },
    badges: {
      insufficientDocsCount:
        kind === BANK_ITEM_KINDS.PROJECT
          ? (insufficientDocsByProjectId || {})[projectId] || 0
          : 0,
    },
    reactions,
    reactionCount: reactions.reduce(
      (total, reaction) => total + (reaction.count || 0),
      0
    ),
    viewCount: isPublicFile ? resolveViewCount(source) : 0,
    // Where the photo is placed on the project's floorplans, for the pin disc
    // of the gallery view: `{count, names}`, or null when it is on none.
    floorplanPins: isPublicFile
      ? (floorplanPinsByFileId || {})[id] || null
      : null,
    path: source.path ?? null,
    parentId: source.parent ?? source.parentFolderId ?? null,
    projectId,
  };
}

/**
 * Picker-dialog format gate: folders always pass; files pass when the list is
 * empty or any token is included in the mime type or equals the extension or
 * format key. Null-safe for items whose file has no mime type.
 * @param {Object} item BankItem
 * @param {Array<string>} [allowedFileFormats]
 * @returns {boolean}
 */
export function matchesAllowedFormats(item, allowedFileFormats) {
  if (!item) return false;
  if (item.type === 'FOLDER') return true;
  if (!Array.isArray(allowedFileFormats) || allowedFileFormats.length === 0) {
    return true;
  }
  const mime = toLowerString(item.mimeType);
  const extension = toLowerString(item.extension);
  const formatKey = toLowerString(item.formatKey);
  return allowedFileFormats.some((format) => {
    if (typeof format !== 'string' || format === '') return false;
    const token = format.toLowerCase();
    return mime.includes(token) || token === extension || token === formatKey;
  });
}
